import React, { useState, useRef } from "react"
import { Popover, PopoverBody } from "reactstrap"
import { FaChevronDown } from "react-icons/fa"
import CloseIcon from "../../../assets/icons/icon-popover-close.svg"
import { ARTICLE_PATHS } from "./utils/constants/articleUrls"
import { resolveUrlForArticles } from "./utils/urlUtils"
import { formatDisplayDate, formatRemainingTime } from "./utils/displayUtils"
import { useProgramDeadlines } from "./hooks/useProgramDeadlines"
import {
  LevelProgressLink,
  PopoverContainer,
  PopoverHeader,
  PopoverTitle,
  PopoverCloseButton,
  PopoverLevelSection,
  PopoverLevelTitle,
  PopoverLevelContent,
  PopoverDateRow,
  PopoverDateLabel,
  PopoverDateValue,
  PopoverNotStarted,
  PopoverDivider,
  PopoverButton,
  PopoverButtonText,
  PopoverGlobalStyles,
  StatusBadgeCheckmark,
} from "./Styles/ProgressTracker.styles"

const formatDateForDisplay = dateString => {
  const formattedDate = formatDisplayDate(dateString)
  return formattedDate || "Not Started"
}

const LevelProgressPopover = () => {
  const [popoverOpen, setPopoverOpen] = useState(false)
  const targetRef = useRef(null)
  const { levelsData, associateProgram } = useProgramDeadlines()

  const toggle = () => setPopoverOpen(!popoverOpen)

  return (
    <>
      <PopoverGlobalStyles />
      <LevelProgressLink
        as="button"
        ref={targetRef}
        onClick={toggle}
        id="datesProgressPopover"
      >
        View My Deadlines
        <FaChevronDown style={{ width: 10, height: 10 }} />
      </LevelProgressLink>

      <Popover
        placement="bottom-end"
        isOpen={popoverOpen}
        target={targetRef}
        toggle={toggle}
        trigger="legacy"
        modifiers={{
          preventOverflow: { enabled: false },
          flip: { enabled: false },
        }}
      >
        <PopoverBody>
          <PopoverContainer>
            <PopoverHeader>
              <PopoverTitle>Associate Program Deadlines</PopoverTitle>
              <PopoverCloseButton onClick={toggle} aria-label="Close">
                <img src={CloseIcon} alt="" />
              </PopoverCloseButton>
            </PopoverHeader>

            {levelsData.map((level, index) => {
              const isLastLevel = index === levelsData.length - 1
              const timeRemaining = formatRemainingTime(level.expirationDate)

              return (
                <React.Fragment key={level.levelKey}>
                  <PopoverLevelSection>
                    <PopoverLevelTitle>
                      {level.isCompleted && (
                        <StatusBadgeCheckmark color="#2CA24B" />
                      )}
                      {level.levelName}
                    </PopoverLevelTitle>
                    <PopoverLevelContent>
                      {level.isStarted ? (
                        <>
                          <PopoverDateRow>
                            <PopoverDateLabel>Started:</PopoverDateLabel>
                            <PopoverDateValue>
                              {formatDateForDisplay(level.startDate)}
                            </PopoverDateValue>
                          </PopoverDateRow>

                          {level.isCompleted ? (
                            <PopoverDateRow>
                              <PopoverDateLabel isCompleted>
                                Completed:
                              </PopoverDateLabel>
                              <PopoverDateValue isCompleted>
                                {formatDateForDisplay(level.completionDate)}
                              </PopoverDateValue>
                            </PopoverDateRow>
                          ) : (
                            <>
                              <PopoverDateRow>
                                <PopoverDateLabel>Deadline:</PopoverDateLabel>
                                <PopoverDateValue>
                                  {formatDateForDisplay(level.expirationDate)}
                                </PopoverDateValue>
                              </PopoverDateRow>
                              {timeRemaining && (
                                <PopoverDateRow>
                                  <PopoverDateLabel>
                                    Time Remaining:
                                  </PopoverDateLabel>
                                  <PopoverDateValue>
                                    {timeRemaining}
                                  </PopoverDateValue>
                                </PopoverDateRow>
                              )}
                            </>
                          )}
                        </>
                      ) : (
                        <PopoverNotStarted>Not Started</PopoverNotStarted>
                      )}
                    </PopoverLevelContent>
                  </PopoverLevelSection>
                  {!isLastLevel && <PopoverDivider />}
                </React.Fragment>
              )
            })}
            <PopoverDivider />

            <PopoverLevelSection isLastSection>
              <PopoverLevelTitle>
                {associateProgram.isCompleted && (
                  <StatusBadgeCheckmark color="#2CA24B" />
                )}
                Election to Membership Deadline
              </PopoverLevelTitle>
              <PopoverLevelContent>
                {associateProgram.startDate && (
                  <PopoverDateRow>
                    <PopoverDateLabel>Started:</PopoverDateLabel>
                    <PopoverDateValue>
                      {formatDateForDisplay(associateProgram.startDate)}
                    </PopoverDateValue>
                  </PopoverDateRow>
                )}
                {associateProgram.isCompleted ? (
                  <PopoverDateRow>
                    <PopoverDateLabel isCompleted>Completed:</PopoverDateLabel>
                    <PopoverDateValue isCompleted>
                      {formatDateForDisplay(associateProgram.completedDate)}
                    </PopoverDateValue>
                  </PopoverDateRow>
                ) : (
                  <>
                    {associateProgram.expirationDate && (
                      <PopoverDateRow>
                        <PopoverDateLabel>Deadline:</PopoverDateLabel>
                        <PopoverDateValue>
                          {formatDateForDisplay(
                            associateProgram.expirationDate,
                          )}
                        </PopoverDateValue>
                      </PopoverDateRow>
                    )}
                  </>
                )}
              </PopoverLevelContent>
            </PopoverLevelSection>

            <PopoverButton
              href={resolveUrlForArticles(ARTICLE_PATHS.TIMEFRAME_DEADLINES)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <PopoverButtonText>Acceptable Progress Guide</PopoverButtonText>
            </PopoverButton>
          </PopoverContainer>
        </PopoverBody>
      </Popover>
    </>
  )
}

export default LevelProgressPopover
